/**
 * GeoExtractor Utilities
 * 
 * Pure-JS extraction logic ported from wenkTagExtractor-production/geo.js.
 * Handles parsing geofence HTML descriptions, normalizing field names,
 * and producing a clean row array for table display.
 */

// ── Arabic → Latin digit conversion ──────────────────────────────────
export const a2e = (s) =>
    s.replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));

// ── Field normalisation map ──────────────────────────────────────────
// Maps every known label (Arabic + English variants) to a standard key.
export const labelMap = {
    'المنطقة': 'area', 'اسم المنطقة': 'area', 'Area': 'area', 'City': 'area', 'Town name': 'area',
    'الفئة': 'classif', 'Class': 'classif',
    'المحلة': 'mahla', 'رقم المحلة': 'mahla', 'Town number': 'mahla', 'Town No.': 'mahla',
    'اسم صاحب المحل': 'driver', 'اسم المسؤول': 'driver', 'Person': 'driver', 'Name': 'driver',
    'الهاتف': 'phone', 'رقم الهاتف': 'phone', 'رقم صاحب المحل': 'phone', 'Phone': 'phone',
    'المندوب': 'mandoob', 'اسم المندوب': 'mandoob', 'اسم صاحب العمل': 'mandoob', 'Uploader': 'mandoob', 'Agent': 'mandoob',
    'وقت التثبيت': 'ttime_raw', 'وقت وتاريخ الاضافة': 'ttime_raw',
    'DateTime': 'ttime_raw', 'DateTimer': 'ttime_raw', 'Upload date': 'ttime_raw',
};

// ── Standard 9-column definitions ────────────────────────────────────
export const standardHeaders = [
    'اسم النقطة', 'المنطقة', 'الفئة', 'المحلة',
    'اسم صاحب المحل', 'الهاتف', 'المندوب',
    'وقت وتاريخ الالتقاط',
];

export const standardDataKeys = [
    'name', 'area', 'classif', 'mahla',
    'driver', 'phone', 'mandoob',
    'captureTime',
];

// English header equivalents (for bilingual UI)
export const standardHeadersEn = [
    'Point Name', 'Area', 'Class', 'Town',
    'Person', 'Phone', 'Agent',
    'Date/Time',
];

// ── Detect if a geofence is WenkTag-formatted ─────────────────────────
export function isWenkTagGeofence(htmlDescr) {
    if (!htmlDescr) return false;

    // Decode HTML entities that may be stored as text
    const tmp = document.createElement('div');
    tmp.innerHTML = htmlDescr;
    const decodedHtml = tmp.textContent || tmp.innerText || '';

    // Only process WenkTag-formatted geofences
    return (
        decodedHtml.includes('<!DOCTYPE html>') ||
        decodedHtml.includes('<center><table ')
    );
}

// ── Parse one zone's HTML description ────────────────────────────────
export function parseZoneHTML(htmlDescr) {
    if (!htmlDescr) return null;

    // Use shared detection logic
    if (!isWenkTagGeofence(htmlDescr)) return null;

    // Decode again for DOMParser (or we could pass it in)
    const tmp = document.createElement('div');
    tmp.innerHTML = htmlDescr;
    const decodedHtml = tmp.textContent || tmp.innerText || '';

    const parser = new DOMParser();
    const doc = parser.parseFromString(decodedHtml, 'text/html');

    const tableSelector = decodedHtml.includes('<!DOCTYPE html>')
        ? '.myTable tr'
        : 'table tr';
    const rows = doc.querySelectorAll(tableSelector);
    if (!rows || rows.length === 0) return null;

    const fields = {};
    const dynamicFields = {};

    rows.forEach((row) => {
        const cells = row.querySelectorAll('td');
        const headers = row.querySelectorAll('th');
        if (cells.length === 1 && headers.length === 1) {
            const label = headers[0].textContent.trim();
            let value = a2e(cells[0].textContent.trim());
            if (!label) return;
            if (labelMap[label]) {
                fields[labelMap[label]] = value;
            } else {
                dynamicFields[label] = value;
            }
        }
    });

    // Extract image URL from the HTML (supports <img> tags)
    let imageUrl = null;
    const imgTag = doc.querySelector('img');
    if (imgTag) {
        imageUrl = imgTag.getAttribute('src') || null;
    }

    return { fields, dynamicFields, imageUrl };
}

// ── Process all zones from a resource ────────────────────────────────
export function processZones(zones, baseUrl = '', sid = '') {
    const rows = [];
    const dynamicLabels = new Set();

    if (!zones || typeof zones !== 'object') return { rows, dynamicLabels };

    const zoneEntries = Array.isArray(zones) ? zones : Object.values(zones);

    for (const zone of zoneEntries) {
        const parsed = parseZoneHTML(zone.d);
        if (!parsed) continue;

        const { fields, dynamicFields } = parsed;

        // Handle date/time merging
        let captureTime = '----';
        if (fields.ttime_raw) {
            captureTime = fields.ttime_raw.replace('ص', 'am').replace('م', 'pm');
        }

        // Extract coordinates from bounding box center (cen_y = lat, cen_x = lng)
        let lat = null;
        let lng = null;
        if (zone.b) {
            if (zone.b.cen_y != null && zone.b.cen_x != null) {
                lat = zone.b.cen_y;
                lng = zone.b.cen_x;
            } else if (zone.b.min_y != null && zone.b.max_y != null) {
                lat = (zone.b.min_y + zone.b.max_y) / 2;
                lng = (zone.b.min_x + zone.b.max_x) / 2;
            }
        }

        // Extract image URL from zone.icon property
        let imageUrl = null;
        if (zone.icon) {
            imageUrl = baseUrl
                ? `${baseUrl}${zone.icon}?b=64&v=1&sid=${sid}`
                : zone.icon;
        }

        const row = {
            name: zone.n || '----',
            area: fields.area || '----',
            classif: fields.classif || '----',
            mahla: fields.mahla || '----',
            driver: fields.driver || '----',
            phone: fields.phone || '----',
            mandoob: fields.mandoob || '----',
            captureTime,
            lat,
            lng,
            imageUrl,
        };

        // Append dynamic fields
        for (const [label, value] of Object.entries(dynamicFields)) {
            row[label] = value;
            dynamicLabels.add(label);
        }

        rows.push(row);
    }

    return { rows, dynamicLabels };
}
